import { fetchOrders } from './api/orders-client';
import { isCalibrated, now as serverNow, offset as clockOffset } from './clock';
import { getOrderLookbackIso } from './config';
import type { OrderStore } from './orders/store';
import { captureException } from './sentry';
import type { ApiOrder, BootstrapConfig, ConnectionState, StoreSettings, WsEvent } from './types';
import { OrderSocket } from './ws/socket';

/**
 * The first orders fetch of a session goes out before we have learned the
 * server clock (see src/clock.ts). If its response reveals that the device
 * clock is off by at least this much, the lookback window we asked for was
 * wrong: refetch once, immediately, with the corrected clock rather than
 * show a wrong set of orders until the next reconciliation.
 */
const CLOCK_SKEW_REFETCH_MS = 30_000;

export interface RuntimeOptions {
  store: OrderStore;
  settings: StoreSettings;
  config: BootstrapConfig;
  xApiKey: string;
  onConnectionChange: (state: ConnectionState) => void;
  onBootstrapped?: () => void;
}

/**
 * Starts the live data layer and keeps it alive for the lifetime of the page.
 *
 * Everything that talks to the network - the WebSocket, the polling and
 * reconciliation timers, in-flight fetches - belongs to one *session*. When
 * the socket reports that the gateway has gone silent (no heartbeat for
 * HEARTBEAT_TIMEOUT_MS, see ws/socket.ts) the current session is torn down
 * and a fresh one is started: new bootstrap fetch, new socket subscription,
 * fresh timers. The OrderStore is shared across sessions, so the screen keeps
 * showing the last known orders until the new session has refreshed them
 * (§3.2). We deliberately do not hard-reload the page here: during a network
 * outage a reload would land on the bootstrap error panel, which never
 * retries (§2.10), and the screen would stay dead until someone touched it.
 */
export function startLiveRuntime(opts: RuntimeOptions): () => void {
  let stopped = false;
  let stopSession: () => void = () => {};

  const restart = (): void => {
    if (stopped) return;
    // eslint-disable-next-line no-console
    console.warn('[runtime] no websocket heartbeat - re-initialising the live runtime');
    captureException(new Error('websocket_heartbeat_timeout'), {
      stage: 'heartbeat',
      storeId: opts.settings.storeId,
    });
    stopSession();
    stopSession = startSession(opts, restart);
  };

  stopSession = startSession(opts, restart);

  return () => {
    stopped = true;
    stopSession();
  };
}

/**
 * One live session: bootstrap fetch, socket, poll / reconcile / sweep timers.
 * Returns a teardown function. After teardown nothing in the session touches
 * the store or schedules work again, even if a fetch was mid-flight when it
 * happened (Chrome 56 has no AbortController, so the fetch itself may well
 * complete).
 *
 * All time-based decisions here are made in server time via `serverNow()`;
 * the TV's own clock is never trusted for the lookback window or the sweep.
 */
function startSession(opts: RuntimeOptions, onStale: () => void): () => void {
  const { store, settings, config, xApiKey, onConnectionChange } = opts;

  let stopped = false;
  let wsHealthy = false;
  let socketOpens = 0;
  // Tracks whether the most recent HTTP fetch reached the server. A network-
  // level failure (TypeError) is an immediate offline signal even before the
  // WebSocket fires its close event.
  let fetchHealthy = true;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let reconcileTimer: ReturnType<typeof setTimeout> | null = null;
  let sweepTimer: ReturnType<typeof setInterval> | null = null;
  // AbortController is only available from Chrome 66; Chrome 56 / Tizen 4 don't
  // have it. The signal is a best-effort cancel on teardown — safe to skip.
  const inflight: { signal: AbortSignal | undefined; abort: () => void } =
    typeof AbortController !== 'undefined'
      ? new AbortController()
      : { signal: undefined, abort: () => {} };

  const reportConnection = (): void => {
    onConnectionChange(wsHealthy && fetchHealthy ? 'online' : 'offline');
  };

  // Lookback window anchored to the *server* clock, recomputed per fetch.
  const lookbackIso = (): string =>
    getOrderLookbackIso(config.orderLookbackMinutes, new Date(serverNow()));

  /**
   * Fetch the lookback window. Every proxy response also calibrates the
   * clock; when the very first calibration of the session shows the device
   * clock was badly skewed, the window we just asked for was computed from
   * the wrong "now" - ask once more with the corrected clock.
   */
  const fetchWindow = async (): Promise<ApiOrder[]> => {
    const wasCalibrated = isCalibrated();
    const orders = await fetchOrders({
      settings,
      xApiKey,
      createdAfter: lookbackIso(),
      signal: inflight.signal,
    });
    if (!wasCalibrated && isCalibrated() && Math.abs(clockOffset()) >= CLOCK_SKEW_REFETCH_MS) {
      return fetchOrders({
        settings,
        xApiKey,
        createdAfter: lookbackIso(),
        signal: inflight.signal,
      });
    }
    return orders;
  };

  const runFetch = async (label: string): Promise<void> => {
    try {
      const orders = await fetchWindow();
      // A session torn down while we were waiting must not touch anything.
      if (stopped) return;
      if (label === 'bootstrap') opts.onBootstrapped?.();
      // Fetch reached the server — clear any previous network-failure flag.
      if (!fetchHealthy) {
        fetchHealthy = true;
        reportConnection();
      }
      store.replaceAll(orders, serverNow());
    } catch (err) {
      if (stopped || (err as Error).name === 'AbortError') return;
      // Transient fetch errors must not blow up the screen. We just
      // surface offline state and let the next tick try again. (§2.10)
      // eslint-disable-next-line no-console
      console.warn(`[runtime] ${label} fetch failed`, err);
      if (label === 'bootstrap') opts.onBootstrapped?.();
      // TypeError = network-level failure (no connectivity). Report offline
      // immediately without waiting for the WebSocket close event.
      if (err instanceof TypeError && fetchHealthy) {
        fetchHealthy = false;
        reportConnection();
      } else if (!wsHealthy) {
        reportConnection();
      }
    }
  };

  const schedulePoll = (): void => {
    if (stopped) return;
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = setTimeout(async () => {
      if (!wsHealthy) {
        await runFetch('poll');
      }
      schedulePoll();
    }, config.fallbackPollMs);
  };

  const scheduleReconcile = (): void => {
    if (stopped) return;
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(async () => {
      await runFetch('reconcile');
      scheduleReconcile();
    }, config.reconciliationMs);
  };

  const socket = new OrderSocket({
    settings,
    xApiKey,
    heartbeatTimeoutMs: config.wsHeartbeatTimeoutMs,
    handlers: {
      onOpen: () => {
        wsHealthy = true;
        fetchHealthy = true;
        reportConnection();
        // A *re*connection: the fallback poll covered the outage only up to its
        // last tick, so recover anything the gateway emitted since then now
        // rather than at the next reconciliation. The first open of a session
        // follows the bootstrap fetch by milliseconds and needs no resync.
        socketOpens += 1;
        if (socketOpens > 1) void runFetch('resync');
      },
      onClose: () => {
        wsHealthy = false;
        reportConnection();
      },
      onError: (err) => {
        captureException(err, { stage: 'websocket' });
      },
      onEvent: (event: WsEvent) => {
        if (event.action === 'order_created') {
          store.upsert(event.data);
        } else if (event.action === 'order_status_updated') {
          store.applyStatus(event.data.orderId, event.data.status, event.timestamp);
        }
      },
      // Heartbeat watchdog fired: hand over to startLiveRuntime for a fresh session.
      onStale,
    },
  });

  // Initial fetch -> then start socket -> then schedule polling/recon.
  void runFetch('bootstrap').then(() => {
    if (stopped) return;
    socket.start();
    schedulePoll();
    scheduleReconcile();
  });

  // Sweep timer prunes expired completed orders every 30s (§3.3), in server time.
  sweepTimer = setInterval(() => store.sweep(serverNow()), 30_000);

  reportConnection();

  return () => {
    stopped = true;
    socket.stop();
    if (pollTimer) clearTimeout(pollTimer);
    if (reconcileTimer) clearTimeout(reconcileTimer);
    if (sweepTimer) clearInterval(sweepTimer);
    inflight.abort();
  };
}
